import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { Building2, Save, Settings } from 'lucide-react';
import { BottomBar, BottomBarSection, Button } from '@bliks-io/ui';
import { useOrganization } from '@/api/queries/useOrganization';
import { useOrgLogoEditor } from '@/hooks/useOrgLogoEditor';
import { usePartnerAdminAccess } from './usePartnerAdminAccess';
import { ContentHeader } from '@/components/content-header';
import {
  OrganizationProfileCard,
  OrganizationProfileCardSkeleton
} from '@/components/organization/organizationProfileCard';
import { PartnerInfoPanel } from '@/pages/partner/components/PartnerInfoPanel';
import { PartnerSettingsTabsNav } from '@/pages/partner/components/PartnerSettingsTabsNav';

export function PartnerOrganizationPage() {
  const { t } = useTranslation();

  const { isPartnerAdmin, isLoading: loadingRole } = usePartnerAdminAccess();
  const { data: org, isLoading: loadingOrg } = useOrganization();
  const logoEditor = useOrgLogoEditor(org?.logo);

  const handleSave = async () => {
    try {
      await logoEditor.save();
      toast.success(t('OrgSettingPage.SettingSaved'));
    } catch (error) {
      toastRequestError(error, t('common.Error'));
    }
  };

  // The route only asserts "is a partner"; the settings area is partner_admin
  // only. The profile's role answers that without a request — CASL on the
  // organization endpoints is the enforcement, this is only what gets rendered.
  if (!loadingRole && !isPartnerAdmin) {
    return (
      <PartnerInfoPanel
        icon={Building2}
        title={t('partner.common.adminOnlyTitle')}
        description={t('partner.settings.adminOnlyDescription')}
        className="p-6"
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PartnerSettingsTabsNav />

      <div className="flex-1 overflow-y-auto p-10">
        <ContentHeader
          icon={Settings}
          title={t('OrgSettingPage.Settings')}
          description={t('OrgSettingPage.editKeyDetails')}
        />

        <div className="flex flex-col gap-6 mt-8">
          {loadingOrg ? (
            <OrganizationProfileCardSkeleton />
          ) : (
            <OrganizationProfileCard
              name={org?.name ?? ''}
              logo={logoEditor.logo}
              file={logoEditor.file}
              error={logoEditor.error}
              disabled={logoEditor.isSaving}
              onRemove={logoEditor.handleRemove}
              onFileChange={logoEditor.handleFileChange}
            />
          )}
        </div>
      </div>

      <BottomBar>
        <BottomBarSection />
        <BottomBarSection>
          <Button
            onClick={handleSave}
            loading={logoEditor.isSaving}
            disabled={logoEditor.isSaving || !logoEditor.isDirty}
          >
            <Save className="w-4 h-4 mr-2" />
            {t('common.Save')}
          </Button>
        </BottomBarSection>
      </BottomBar>
    </div>
  );
}
